import { Disclosure, Transition } from '@headlessui/react'
import { ChevronDownIcon } from '@heroicons/react/20/solid'
import cx from 'classnames'
import { type FC, type ReactNode } from 'react'
import { twMerge } from 'tailwind-merge'
import { type DefaultProps } from '../common'

export interface CollapseProps extends DefaultProps {
  /**
   * Title
   */
  alwaysExpanded?: boolean
  chevronClassName?: string
  title: string | ReactNode
}

export const Collapse: FC<CollapseProps> = ({
  // Custom component properties
  title,
  alwaysExpanded,
  chevronClassName,
  // Common default properties for each component
  id,
  testId,
  ariaLabel,
  children,
  className,
}) => {
  // Component base class names. Can be overriden by className property.
  const baseClass = cx(
    'w-full flex flex-row typo-primary-lg-regular text-text-body justify-between align-center items-center relative',
  )

  // Final class names
  const finalClasses = twMerge(baseClass, className)

  return (
    <div
      className="w-full"
      {...(testId ? { 'data-testid': testId } : {})}
      {...(id ? { id } : {})}
      {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
    >
      <Disclosure>
        {({ open }) => (
          <>
            <Disclosure.Button
              data-testid="collapse-button"
              className={finalClasses}
              aria-expanded={alwaysExpanded ?? open}
              data-expanded={alwaysExpanded ?? open}
            >
              {title}
              {/* Only show Chevron if it can be collapsed */}
              {!alwaysExpanded && (
                <ChevronDownIcon
                  className={cx(
                    'min-h-md min-w-md max-h-md max-w-md',
                    {
                      'rotate-180 transform duration-100': open,
                      'rotate-0 transform duration-100': !open,
                    },
                    chevronClassName && chevronClassName, // Only add chevronClasses if it's truthy
                  )}
                />
              )}
            </Disclosure.Button>
            <Transition
              show={alwaysExpanded ?? open}
              enter="transition duration-100 ease-out"
              enterFrom="transform scale-95 opacity-0"
              enterTo="transform scale-100 opacity-100"
              leave="transition duration-75 ease-out"
              leaveFrom="transform scale-100 opacity-100"
              leaveTo="transform scale-95 opacity-0"
            >
              <Disclosure.Panel data-testid="collapsible-section" static>
                {children}
              </Disclosure.Panel>
            </Transition>
          </>
        )}
      </Disclosure>
    </div>
  )
}
